/**
 * GitHub contributions.
 *
 * Source: github-contributions-api.jogruber.de — public, no auth, no proxy.
 *
 * The productive-weekday server (/api/github-stats) used to supply stars and
 * open-source PRs here, but it now answers 401 and every request was being
 * discarded. It cannot be replaced by LeetCode's GraphQL API — that API only
 * knows about LeetCode, and has no concept of GitHub repos, stars or PRs.
 * If stars/PRs are wanted back, GitHub's own REST API is the source
 * (api.github.com/search/issues?q=author:<user>+type:pr+is:merged), though
 * unauthenticated calls are capped at 60/hour per IP.
 */

const USERNAME = "jugshaurya";
const CACHE_KEY = "gh_data_v2";
const IS_DEV = process.env.NODE_ENV === "development";
const CACHE_TTL = IS_DEV ? 30_000 : 3_600_000;

export interface GHContribution {
  date: string;
  count: number;
}

export interface GHPullRequest {
  title: string;
  repo: string;
  url: string;
  state: "MERGED" | "OPEN" | "CLOSED";
}

export interface GHData {
  totalContributions: number;
  totalStars: number;
  contributions: GHContribution[];
  pullRequests: GHPullRequest[];
  fetchedAt: number;
}

export async function fetchGHData(): Promise<GHData> {
  // Cache check
  try {
    const c = localStorage.getItem(CACHE_KEY);
    if (c) {
      const p = JSON.parse(c) as GHData;
      if (Date.now() - p.fetchedAt < CACHE_TTL) return p;
    }
  } catch {}

  const [contribResult] = await Promise.allSettled([
    fetch(`https://github-contributions-api.jogruber.de/v4/${USERNAME}?y=last`).then(r => r.json()),
  ]);

  // 1. Parse contributions heatmap
  const contributions: GHContribution[] = [];
  let totalContributions = 0;
  if (contribResult.status === "fulfilled" && contribResult.value?.contributions) {
    for (const entry of contribResult.value.contributions) {
      contributions.push({ date: entry.date, count: entry.count });
      totalContributions += entry.count;
    }
  }

  // Stars and PRs came from the productive-weekday server, which is gone.
  // Left empty rather than silently wrong; nothing renders them today.
  const pullRequests: GHPullRequest[] = [];
  const totalStars = 0;

  const data: GHData = {
    totalContributions,
    totalStars,
    contributions,
    pullRequests,
    fetchedAt: Date.now(),
  };

  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
  return data;
}
